import { useCallback, useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Bookmark, Settings, BookOpenText, Mail, Calendar, Brain, Plug, Reply } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { ChatMessage, ChatInboxEmail } from '../../../shared/types/chat'
import type { FeedbackType } from '../types'
import { useChatActions } from '../ChatContext'
import { getImageUrl } from '../api'
import { useEmailDetail } from '../useEmailDetail'
import { parseExternalContent, parseChoiceTags } from '../utils'
import BriefingCard from './BriefingCard'
import GiftCard from './GiftCard'
import ReceiptCard from './ReceiptCard'
import InfoCard from './InfoCard'
import ProgressCard from './ProgressCard'
import ChecklistCard from './ChecklistCard'
import ComparisonCard from './ComparisonCard'
import ApprovalCard from './ApprovalCard'
import BookingBriefCard from './BookingBriefCard'
import ChoiceOptionsCard from './ChoiceOptionsCard'
import EmailNotificationCard from './EmailNotificationCard'
import SentEmailCard from './SentEmailCard'
import QuarantineCard from './QuarantineCard'
import TriageEmailCard from './TriageEmailCard'

const quickActionIcons: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  Settings, BookOpenText, Mail, Calendar, Brain, Plug,
}

interface MessageRendererProps {
  message: ChatMessage
}

export default function MessageRenderer({
  message,
}: MessageRendererProps) {
  const { t } = useTranslation('chat')
  const {
    onApprove,
    onDeny,
    onChoiceSelect,
    onMultiChoiceToggle,
    onMultiChoiceSubmit,
    onChecklistToggle,
    onChecklistDone,
    onComparisonSelect,
    onInfoAction,
    onSendMessage,
    onViewInNotebook,
    onNavigate,
    onLongPress,
  } = useChatActions()

  // Parse <external-content> and <sent-email> from message text
  const parsed = useMemo(
    () => parseExternalContent(message.content),
    [message.content],
  )
  // Messages inserted by the email pipeline are linked to the source email via
  // sourceEmailId; lazy-fetch the detail so we can render a card instead of the
  // raw <external-content-*> wrapper still stored in content for the LLM.
  const lazyEmail = useEmailDetail(message.sourceEmailId)
  const loadingEmail: ChatInboxEmail | null = message.sourceEmailId && !lazyEmail
    ? { sender: '', senderEmail: '', subject: '…', body: '', loading: true }
    : null
  const email = message.inboxEmail ?? lazyEmail ?? loadingEmail ?? parsed.email
  const sentEmail = message.sentEmail ?? parsed.sentEmail
  const suppressRawContent = !!message.sourceEmailId
  const rawContent = suppressRawContent
    ? ''
    : (parsed.email || parsed.sentEmail) ? parsed.cleanContent : message.content
  // Messages with external content always render as assistant style
  const isAssistant = message.type === 'assistant' || !!email || !!sentEmail

  // Parse [choice] tags from assistant messages. Always strip the tags from the
  // displayed text; fall back to parsed choices only when the backend didn't set
  // choiceOptions itself (via the present_options tool).
  const parsedChoices = useMemo(() => {
    if (message.type !== 'assistant') return null
    return parseChoiceTags(rawContent)
  }, [rawContent, message.type])

  const displayContent = parsedChoices?.cleanContent ?? rawContent
  const effectiveChoiceOptions = message.choiceOptions ??
    (parsedChoices?.choices.length ? parsedChoices.choices : undefined)

  // Resolve image URLs: use pre-loaded imageUrls, or fetch signed URLs for history messages
  const [fetchedImageUrls, setFetchedImageUrls] = useState<string[]>([])
  useEffect(() => {
    if (message.imageUrls?.length || !message.imageIds?.length) return
    let cancelled = false
    Promise.all(message.imageIds.map(id => getImageUrl(id).catch(() => null)))
      .then(results => {
        if (cancelled) return
        setFetchedImageUrls(
          results.filter(Boolean).map(r => r!.thumbnailUrl || r!.url)
        )
      })
    return () => { cancelled = true }
  }, [message.imageIds, message.imageUrls])
  const resolvedImageUrls = message.imageUrls?.length ? message.imageUrls : fetchedImageUrls

  // Tap on a bubble opens the message context menu. Long-press is intentionally
  // left to the OS for native text selection. The tap handler bails when the
  // user has dragged to select text or clicked a link/button.
  const handleBubbleTap = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    if (target.closest('a, button')) return
    if (window.getSelection()?.toString()) return
    onLongPress(message.id)
  }, [onLongPress, message.id])

  return (
    <div className="max-w-[85%] sm:max-w-[75%] lg:max-w-[70%] space-y-2">
      {/* Inline Email Notification */}
      {email && (
        <>
          <EmailNotificationCard email={email} />
          {!email.status && (
            <div className="flex gap-2 px-1">
              <button
                onClick={() => onSendMessage(`Reply to email from ${email.sender}`)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gold/20 text-gold/70 text-xs hover:bg-gold/10 hover:border-gold/40 transition-colors"
              >
                <Reply size={12} />
                {t('email.reply')}
              </button>
            </div>
          )}
        </>
      )}

      {/* Sent Email Card */}
      {sentEmail && (
        <SentEmailCard email={sentEmail} />
      )}

      {/* Quarantine Card */}
      {message.quarantineEmail && (
        <QuarantineCard
          email={message.quarantineEmail}
          onRelease={() => onSendMessage(`Release quarantined email from ${message.quarantineEmail!.sender}`)}
          onBlock={() => onSendMessage(`Block sender ${message.quarantineEmail!.senderEmail}`)}
        />
      )}

      {/* Triage Email Card */}
      {message.triageEmail && (
        <TriageEmailCard email={message.triageEmail} messageId={message.id} />
      )}

      {/* User message images */}
      {resolvedImageUrls.length > 0 && (
        <div className={`flex flex-wrap gap-1.5 ${resolvedImageUrls.length === 1 ? '' : 'max-w-[280px]'}`}>
          {resolvedImageUrls.map((url, i) => (
            <img
              key={i}
              src={url}
              alt=""
              className={`rounded-xl border border-gold/15 object-cover ${
                resolvedImageUrls.length === 1
                  ? 'max-w-[280px] max-h-[280px]'
                  : 'w-[calc(50%-3px)] aspect-square'
              }`}
            />
          ))}
        </div>
      )}

      {/* Briefing Card or Message Bubble (suppress text when a card is shown) */}
      {message.briefingCard ? (
        <BriefingCard messageId={message.id} briefing={message.briefingCard} />
      ) : message.triageEmail ? null : displayContent ? (
        <div
          onClick={handleBubbleTap}
          className={`px-4 py-2.5 cursor-default caret-transparent ${
            isAssistant
              ? 'glass-card rounded-[18px_18px_18px_4px] text-ivory/90'
              : 'bubble-user rounded-[18px_18px_4px_18px] text-ivory'
          }`}
        >
          {isAssistant ? (
            <div className="text-sm leading-relaxed prose-chat">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{displayContent}</ReactMarkdown>
            </div>
          ) : (
            <p className="text-sm leading-relaxed whitespace-pre-line">{displayContent}</p>
          )}
          <div className="flex justify-end mt-1 -mb-1">
            <span className={`text-[10px] tabular-nums ${isAssistant ? 'text-pearl/45' : 'text-ivory/55'}`}>
              {message.displayTime || message.timestamp}
            </span>
          </div>
        </div>
      ) : null}

      {/* Gift Suggestions */}
      {message.giftSuggestions && (
        <div className="space-y-2 mt-2">
          <div className="grid grid-cols-1 gap-2">
            {message.giftSuggestions.map((gift) => (
              <GiftCard
                key={gift.id}
                gift={gift}
                onFeedback={(id: string, feedback: FeedbackType) => {
                  console.log('Gift feedback:', id, feedback)
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Receipt */}
      {message.receipt && (
        <div className="mt-2">
          <ReceiptCard receipt={message.receipt} />
        </div>
      )}

      {/* Progress Tracker */}
      {message.progress && (
        <div className="mt-2">
          <ProgressCard progress={message.progress} />
        </div>
      )}

      {/* Info Card */}
      {message.infoCard && (
        <div className="mt-2">
          <InfoCard
            info={message.infoCard}
            onAction={(actionId: string, actionLabel: string, linkedNoteId?: string) => onInfoAction(message.id, actionId, actionLabel, linkedNoteId)}
          />
        </div>
      )}

      {/* Comparison Card */}
      {message.comparison && (
        <div className="mt-2">
          <ComparisonCard
            comparison={message.comparison}
            selectedOption={message.selectedComparison}
            onSelect={(optionId: string, optionName: string) => onComparisonSelect(message.id, optionId, optionName)}
          />
        </div>
      )}

      {/* Checklist */}
      {message.checklist && (
        <div className="mt-2">
          <ChecklistCard
            checklist={message.checklist}
            onToggle={(itemId: string) => onChecklistToggle(message.id, itemId)}
            onDone={() => onChecklistDone(message.id)}
          />
        </div>
      )}

      {/* Choice Options — skip when an ApprovalCard handles the action */}
      {effectiveChoiceOptions && !message.approval && (
        <ChoiceOptionsCard
          options={effectiveChoiceOptions}
          multiSelect={message.multiSelect}
          selectedChoice={message.selectedChoice}
          selectedChoices={message.selectedChoices}
          multiChoiceSubmitted={message.multiChoiceSubmitted}
          onSelect={(option) => onChoiceSelect(message.id, option)}
          onMultiToggle={(optionId) => onMultiChoiceToggle(message.id, optionId)}
          onMultiSubmit={() => onMultiChoiceSubmit(message.id)}
        />
      )}

      {/* Approval Card */}
      {message.approval && (
        <ApprovalCard
          approval={message.approval}
          messageId={message.id}
          onApprove={onApprove}
          onDeny={onDeny}
        />
      )}

      {/* Booking Brief Card */}
      {message.bookingBrief && (
        <BookingBriefCard
          bookingBrief={message.bookingBrief}
          messageId={message.id}
        />
      )}

      {/* Quick Action Buttons */}
      {message.quickActions && message.quickActions.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2 px-1">
          {message.quickActions.map(action => {
            const Icon = quickActionIcons[action.icon]
            return (
              <button
                key={action.id}
                onClick={() => onNavigate(action.navigate)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gold/20 text-gold/70 text-xs hover:bg-gold/10 hover:border-gold/40 transition-colors"
              >
                {Icon && <Icon size={12} />}
                {action.label}
              </button>
            )
          })}
        </div>
      )}

      {/* Working indicator */}
      {message.isWorking && (
        <div className="flex items-center gap-2 px-2">
          <div className="w-1.5 h-1.5 rounded-full bg-amber animate-pulse" />
          <span className="text-xs text-amber/70">{t('working')}</span>
        </div>
      )}

      {message.type === 'assistant' && message.savedToNotebook && (
        <div className="flex items-center gap-2 px-2">
          <button
            onClick={() => message.notebookNoteId && onViewInNotebook(message.notebookNoteId)}
            className="text-gold hover:text-gold-light transition-colors flex items-center gap-0.5"
            title={t('message.viewInNotebook')}
          >
            <Bookmark size={12} />
            <span className="text-xs">{t('message.viewInNotebook')}</span>
          </button>
        </div>
      )}
    </div>
  )
}
